import { CartLinesUpdateEvent } from '@shopify/events';

const formatMoney = (cents) => {
  const amount = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return '$' + amount;
};

const init = () => {
  document.querySelectorAll('[data-kaw-product]').forEach((root) => {
    if (root.dataset.kawReady) return;
    root.dataset.kawReady = 'true';

    const qtyInput = root.querySelector('[data-kaw-qty]');
    root.querySelector('[data-kaw-qty-minus]')?.addEventListener('click', () => {
      qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
    });
    root.querySelector('[data-kaw-qty-plus]')?.addEventListener('click', () => {
      qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
    });

    const dataEl = root.querySelector('[data-kaw-variants]');
    const selects = Array.from(root.querySelectorAll('[data-kaw-option]'));
    const variants = dataEl ? JSON.parse(dataEl.textContent) : [];

    const priceEl = root.querySelector('[data-kaw-price]');
    const skuEl = root.querySelector('[data-kaw-sku]');
    const stockEl = root.querySelector('[data-kaw-stock]');
    const stockLabel = root.querySelector('[data-kaw-stock-label]');
    const idInput = root.querySelector('[data-kaw-variant-id]');
    const atc = root.querySelector('[data-kaw-atc]');
    const imageEl = root.querySelector('[data-kaw-product-image]');
    const imageGhost = root.querySelector('[data-kaw-product-image-ghost]');
    const inStockText = stockLabel ? stockLabel.textContent.trim() : 'In stock';

    // Variant id -> current cart line quantity, seeded server-side. Only consulted when
    // the shopper arrived via the cart drawer's edit icon (?update_cart=1) - landing on
    // a PDP any other way (browsing, a direct link) always shows "Add to Cart", even for
    // a variant that happens to already be in the cart.
    const cameFromCartEdit = new URLSearchParams(window.location.search).has('update_cart');
    let cartLines = {};
    if (cameFromCartEdit) {
      try {
        cartLines = JSON.parse(root.dataset.kawCartLines || '{}');
      } catch {
        cartLines = {};
      }
    }

    const updateAtcMode = (variantId) => {
      if (!atc) return;
      const inCart = cameFromCartEdit && Object.prototype.hasOwnProperty.call(cartLines, String(variantId));
      atc.dataset.kawMode = inCart ? 'update' : 'add';
      atc.textContent = inCart ? atc.dataset.kawAtcUpdateLabel : atc.dataset.kawAtcAddLabel;
    };
    updateAtcMode(idInput?.value);

    const thumbs = Array.from(root.querySelectorAll('[data-kaw-thumb]'));
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let isDragging = false;
    let activeThumbIndex = Math.max(
      0,
      thumbs.findIndex((t) => t.classList.contains('kaw-product-view__thumb--active'))
    );
    const setMainImage = ({ src, alt, width, height, full }, options = {}) => {
      if (!imageEl) return;
      // Fetch the new image right away, in parallel with the slide/fade animation, so it's
      // already decoded by the time the transition reaches the swap - otherwise a slow
      // network request stalls mid-animation and the motion reads as janky.
      if (src) new Image().src = src;
      const matchedIndex = thumbs.findIndex((t) => t.dataset.src === src);
      if (matchedIndex >= 0) activeThumbIndex = matchedIndex;
      thumbs.forEach((t) => t.classList.toggle('kaw-product-view__thumb--active', t.dataset.src === src));

      const applyImage = () => {
        imageEl.src = src;
        imageEl.alt = alt;
        imageEl.width = width;
        imageEl.height = height;
        imageEl.dataset.full = full || src;
      };

      if (prefersReducedMotion.matches || options.instant) {
        // options.instant: the drag/swipe handler already animated the visual transition
        // itself (see below) - this just needs to commit the state, no extra animation.
        applyImage();
        return;
      }

      // Crossfade: fade the current image out, swap the src while invisible, fade the
      // new one in - mirrors the live site's Fotorama slide transition on thumbnail change.
      imageEl.addEventListener(
        'transitionend',
        () => {
          applyImage();
          imageEl.classList.remove('kaw-product-view__image--fading');
        },
        { once: true }
      );
      imageEl.classList.add('kaw-product-view__image--fading');
    };

    // Shared by keyboard arrows and (eventually) lightbox nav - stops at the first/last
    // image instead of wrapping around.
    const stepImage = (delta, options = {}) => {
      const nextIndex = activeThumbIndex + delta;
      if (nextIndex < 0 || nextIndex >= thumbs.length) return null;
      activeThumbIndex = nextIndex;
      const next = thumbs[activeThumbIndex];
      setMainImage(next.dataset, options);
      return next;
    };

    // Slide-in transition for a direct thumbnail pick - the same connected two-image motion
    // as the live drag (entering from the side the thumbnail sits on), instead of a crossfade.
    const slideToImage = (targetIndex) => {
      if (!imageGhost || targetIndex === activeThumbIndex || !thumbs[targetIndex]) return;
      const direction = targetIndex > activeThumbIndex ? 1 : -1;
      const target = thumbs[targetIndex];

      if (prefersReducedMotion.matches) {
        setMainImage(target.dataset, { instant: true });
        return;
      }

      imageGhost.src = target.dataset.src;
      imageGhost.alt = target.dataset.alt || '';
      imageEl.classList.add('kaw-product-view__image--no-transition');
      imageGhost.classList.add('kaw-product-view__image--no-transition');
      imageEl.style.transform = 'translateX(0)';
      imageGhost.style.transform = `translateX(${direction > 0 ? '100%' : '-100%'})`;
      imageGhost.hidden = false;
      void imageGhost.offsetWidth; // flush the entry position before animating
      imageEl.classList.remove('kaw-product-view__image--no-transition');
      imageGhost.classList.remove('kaw-product-view__image--no-transition');

      imageEl.style.transform = `translateX(${direction > 0 ? '-100%' : '100%'})`;
      imageGhost.style.transform = 'translateX(0)';

      imageEl.addEventListener(
        'transitionend',
        () => {
          setMainImage(target.dataset, { instant: true });
          imageEl.classList.add('kaw-product-view__image--no-transition');
          imageGhost.classList.add('kaw-product-view__image--no-transition');
          imageEl.style.transform = '';
          imageGhost.style.transform = '';
          imageGhost.hidden = true;
          void imageEl.offsetWidth;
          imageEl.classList.remove('kaw-product-view__image--no-transition');
          imageGhost.classList.remove('kaw-product-view__image--no-transition');
        },
        { once: true }
      );
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => slideToImage(index));
      // Arrow-key gallery nav with loop, matching the live site's Fotorama "keyboard"/"loop" options.
      thumb.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        stepImage(event.key === 'ArrowRight' ? 1 : -1)?.focus();
      });
    });

    // Click the main image to open the fullscreen lightbox - suppressed below when the
    // click was actually the end of a drag/swipe, so swiping never also pops the lightbox.
    const zoomTrigger = root.querySelector('[data-kaw-zoom-trigger]');
    const lightboxEl = root.querySelector('kaw-product-lightbox');
    let suppressZoomClick = false;
    zoomTrigger?.addEventListener('click', () => {
      if (suppressZoomClick) {
        suppressZoomClick = false;
        return;
      }
      if (typeof lightboxEl?.open === 'function') lightboxEl.open(activeThumbIndex);
    });

    // Drag/swipe the main image left or right - the incoming photo tracks the cursor in
    // real time right alongside the outgoing one (connected motion, like a carousel), so it
    // starts appearing the moment the drag begins instead of only after release.
    const mediaEl = root.querySelector('.kaw-product-view__media');
    if (mediaEl && imageGhost && thumbs.length > 1) {
      const DRAG_THRESHOLD = 40;
      const DRAG_LOCK_THRESHOLD = 5;
      const BOUNDARY_RESISTANCE = 0.3;
      let dragStartX = null;
      let dragDirection = 0; // 1 = next, -1 = prev, 0 = not yet decided this gesture
      let boundaryDrag = false; // dragging further than the first/last image - rubber-band only

      const endDrag = () => {
        dragStartX = null;
        dragDirection = 0;
        boundaryDrag = false;
        isDragging = false;
      };

      const resetImages = () => {
        imageEl.classList.add('kaw-product-view__image--no-transition');
        imageGhost.classList.add('kaw-product-view__image--no-transition');
        imageEl.style.transform = '';
        imageGhost.style.transform = '';
        imageGhost.hidden = true;
        void imageEl.offsetWidth; // flush the instant reset before re-enabling the transition
        imageEl.classList.remove('kaw-product-view__image--no-transition');
        imageGhost.classList.remove('kaw-product-view__image--no-transition');
      };

      mediaEl.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary) return;
        // Thumbnails live inside mediaEl too - don't hijack their own click/pointer handling.
        if (event.target instanceof Element && event.target.closest('[data-kaw-thumb]')) return;
        dragStartX = event.clientX;
      });

      mediaEl.addEventListener('pointermove', (event) => {
        if (dragStartX === null) return;
        const deltaX = event.clientX - dragStartX;

        if (!dragDirection && !boundaryDrag) {
          if (Math.abs(deltaX) < DRAG_LOCK_THRESHOLD) return;
          const proposedDirection = deltaX < 0 ? 1 : -1;
          const targetIndex = activeThumbIndex + proposedDirection;
          isDragging = true;
          // Only capture once a real drag is confirmed - capturing on every pointerdown
          // (even a plain click) stops the browser from pairing pointerdown/up on the same
          // element, which silently kills the native click that opens the lightbox.
          mediaEl.setPointerCapture(event.pointerId);
          // Hide the hover magnifier (declared further below) if it was showing when the drag started.
          root.querySelector('[data-kaw-magnifier-lens]')?.setAttribute('hidden', '');
          root.querySelector('[data-kaw-magnifier]')?.setAttribute('hidden', '');

          if (targetIndex < 0 || targetIndex >= thumbs.length) {
            // Already at the first/last image - rubber-band the current one instead of sliding.
            boundaryDrag = true;
            imageEl.classList.add('kaw-product-view__image--no-transition');
          } else {
            dragDirection = proposedDirection;
            const target = thumbs[targetIndex];
            imageGhost.src = target.dataset.src;
            imageGhost.alt = target.dataset.alt || '';
            imageGhost.hidden = false;
            imageEl.classList.add('kaw-product-view__image--no-transition');
            imageGhost.classList.add('kaw-product-view__image--no-transition');
          }
        }

        if (boundaryDrag) {
          imageEl.style.transform = `translateX(${deltaX * BOUNDARY_RESISTANCE}px)`;
          return;
        }

        const edgePercent = dragDirection > 0 ? 100 : -100;
        imageEl.style.transform = `translateX(${deltaX}px)`;
        imageGhost.style.transform = `translateX(calc(${edgePercent}% + ${deltaX}px))`;
      });

      mediaEl.addEventListener('pointerup', (event) => {
        if (dragStartX === null) return;
        const deltaX = event.clientX - dragStartX;
        const direction = dragDirection;
        const wasBoundaryDrag = boundaryDrag;
        endDrag();

        if (wasBoundaryDrag) {
          // Nothing further that way - spring the image back to its resting position.
          suppressZoomClick = true;
          imageEl.classList.remove('kaw-product-view__image--no-transition');
          imageEl.style.transform = 'translateX(0)';
          imageEl.addEventListener('transitionend', resetImages, { once: true });
          return;
        }

        if (!direction) return; // never moved enough to count as a drag - let the click through

        suppressZoomClick = true;
        imageEl.classList.remove('kaw-product-view__image--no-transition');
        imageGhost.classList.remove('kaw-product-view__image--no-transition');

        const committed = Math.abs(deltaX) >= DRAG_THRESHOLD;
        if (committed) {
          imageEl.style.transform = `translateX(${direction > 0 ? '-100%' : '100%'})`;
          imageGhost.style.transform = 'translateX(0)';
        } else {
          imageEl.style.transform = 'translateX(0)';
          imageGhost.style.transform = `translateX(${direction > 0 ? '100%' : '-100%'})`;
        }

        imageEl.addEventListener(
          'transitionend',
          () => {
            if (committed) {
              const target = thumbs[activeThumbIndex + direction];
              setMainImage(target.dataset, { instant: true });
            }
            resetImages();
          },
          { once: true }
        );
      });

      mediaEl.addEventListener('pointercancel', () => {
        resetImages();
        endDrag();
      });
    }

    // Hover magnifier (desktop/fine-pointer only) - mirrors the live site's Fotorama
    // magnify-on-hover: a lens follows the cursor over the image, a fixed panel to the
    // right shows the corresponding zoomed-in crop of the high-res image.
    const magnifierLens = root.querySelector('[data-kaw-magnifier-lens]');
    const magnifierPanel = root.querySelector('[data-kaw-magnifier]');
    const canMagnify = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (canMagnify && magnifierLens && magnifierPanel && mediaEl && zoomTrigger && imageEl) {
      const ZOOM_FACTOR = 2.5;
      const MAGNIFIER_GAP = 60;

      // The lens/panel only activate over the image itself (zoomTrigger), not the thumbnail
      // strip below it, even though both share the same positioned ancestor (mediaEl).
      zoomTrigger.addEventListener('mouseenter', () => {
        if (isDragging) return;
        const triggerRect = zoomTrigger.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        // Landscape pane (wider than tall), flush against the right edge of the viewport -
        // matches the live site's large zoom-out pane. Positioned absolute (not fixed)
        // relative to the section root, so it scrolls with the page instead of staying put.
        const availableWidth = window.innerWidth - triggerRect.right - MAGNIFIER_GAP;
        const panelWidth = Math.max(availableWidth, 360);
        const panelHeight = panelWidth * 0.4 * 1.2;
        magnifierPanel.style.width = `${panelWidth}px`;
        magnifierPanel.style.height = `${panelHeight}px`;
        magnifierPanel.style.backgroundImage = `url(${imageEl.dataset.full || imageEl.currentSrc || imageEl.src})`;
        // Anchor to the viewport's right edge.
        magnifierPanel.style.left = `${window.innerWidth - rootRect.left - panelWidth}px`;
        magnifierPanel.style.top = `${triggerRect.top - rootRect.top + triggerRect.height * 0.25}px`;
        magnifierLens.hidden = false;
        magnifierPanel.hidden = false;
      });

      zoomTrigger.addEventListener('mousemove', (event) => {
        if (isDragging) return;
        // Keep in sync with the active photo - it can change (thumbnail click, keyboard,
        // drag) without the cursor ever leaving the image, so mouseenter alone would go stale.
        magnifierPanel.style.backgroundImage = `url(${imageEl.dataset.full || imageEl.currentSrc || imageEl.src})`;
        const mediaRect = mediaEl.getBoundingClientRect();
        const triggerRect = zoomTrigger.getBoundingClientRect();
        const lensSize = magnifierLens.offsetWidth;

        const x = Math.min(Math.max(event.clientX - triggerRect.left - lensSize / 2, 0), triggerRect.width - lensSize);
        const y = Math.min(Math.max(event.clientY - triggerRect.top - lensSize / 2, 0), triggerRect.height - lensSize);
        // The lens is absolutely positioned relative to mediaEl, so offset by zoomTrigger's
        // own position within it.
        magnifierLens.style.left = `${x + (triggerRect.left - mediaRect.left)}px`;
        magnifierLens.style.top = `${y + (triggerRect.top - mediaRect.top)}px`;

        const panelWidth = magnifierPanel.offsetWidth;
        const panelHeight = magnifierPanel.offsetHeight;
        magnifierPanel.style.backgroundSize = `${panelWidth * ZOOM_FACTOR}px ${panelHeight * ZOOM_FACTOR}px`;
        magnifierPanel.style.backgroundPosition = `-${(x / (triggerRect.width - lensSize)) * (panelWidth * ZOOM_FACTOR - panelWidth)}px -${
          (y / (triggerRect.height - lensSize)) * (panelHeight * ZOOM_FACTOR - panelHeight)
        }px`;
      });

      zoomTrigger.addEventListener('mouseleave', () => {
        magnifierLens.hidden = true;
        magnifierPanel.hidden = true;
      });
    }

    if (variants.length && selects.length) {
      const update = () => {
        const chosen = selects.map((s) => s.value);
        const variant = variants.find((v) => v.options.every((opt, i) => opt === chosen[i]));
        if (variant) {
          idInput.value = variant.id;
          if (priceEl) priceEl.textContent = formatMoney(variant.price);
          if (skuEl) skuEl.textContent = variant.sku;
          if (imageEl && variant.image) {
            setMainImage(variant.image);
          }
        }
        const available = !!variant && variant.available;
        if (atc) atc.disabled = !available;
        if (stockEl) stockEl.classList.toggle('kaw-product-view__stock--out', !available);
        if (stockLabel) stockLabel.textContent = available ? inStockText : 'Out of stock';
        if (variant) updateAtcMode(variant.id);
      };

      selects.forEach((s) => s.addEventListener('change', update));
      update();
    }

    const animationEnabled = root.dataset.kawAddToCartAnimation === 'true';

    const animateFlyToCart = () => {
      // More than one cart icon can exist at once (desktop utility bar + mobile header), only
      // one visible per breakpoint - an offscreen/hidden one would send the animation nowhere near it.
      const cartIcon = [
        ...document.querySelectorAll('[data-kaw-cart-icon], .header-actions__cart-icon'),
      ].find((el) => el.getClientRects().length > 0);
      if (!cartIcon || !imageEl) return;

      const flyToCartElement = document.createElement('fly-to-cart');
      // `fly-to-cart--main`'s easing has an extreme x-overshoot tuned for Horizon's stock
      // button-to-cart distance; flying the full product image from this custom PDP made that
      // overshoot read as "flies left" instead of up. `--quick`'s gentler curve looks right here.
      flyToCartElement.classList.add('fly-to-cart--quick');
      flyToCartElement.style.setProperty('background-image', `url(${imageEl.currentSrc || imageEl.src})`);
      flyToCartElement.style.setProperty('--start-opacity', '0');
      flyToCartElement.useSourceSize = true;
      flyToCartElement.sourceScale = 1.2;
      flyToCartElement.source = imageEl;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);
    };

    const form = root.querySelector('[data-kaw-form]');
    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (atc?.disabled) return;

      const isUpdate = atc?.dataset.kawMode === 'update';
      atc.disabled = true;
      if (animationEnabled && !isUpdate) animateFlyToCart();

      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity'), 10) || 1;
      const deferred = CartLinesUpdateEvent.createPromise();

      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: isUpdate ? 'update' : 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity }],
          promise: deferred.promise,
        })
      );

      try {
        let addResponse;
        if (isUpdate) {
          addResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: variantId, quantity }),
          });
        } else {
          addResponse = await fetch(`${form.action}.js`, {
            method: 'POST',
            body: formData,
            headers: { Accept: 'application/json' },
          });
        }
        const added = await addResponse.json();
        if (!addResponse.ok) throw new Error(added.description || added.message || 'Add to cart failed');

        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        const cart = await cartResponse.json();

        if (quantity > 0) {
          cartLines[String(variantId)] = quantity;
        } else {
          delete cartLines[String(variantId)];
        }
        updateAtcMode(variantId);

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: cart.item_count, source: 'kaw-product-view' },
        });
      } catch (error) {
        deferred.reject(error);
        console.error('[kaw-product-view] add to cart failed:', error);
      } finally {
        atc.disabled = false;
      }
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
document.addEventListener('shopify:section:load', init);
